"use client";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExpandIcon,
  MinusIcon,
  PlusIcon,
} from "lucide-react";
import { useRef, useState, type KeyboardEvent } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type RecordImage = {
  id: string;
  fileId: string;
  label: string;
  fileName: string;
};

/** A read-only viewer: navigation never changes the record or its file references. */
export function RecordImageViewer({ images }: { images: RecordImage[] }) {
  // A new record/file selection must not inherit the previous selection or zoom.
  return images.length ? (
    <ImageViewer
      key={JSON.stringify(images.map((image) => image.id))}
      images={images}
    />
  ) : null;
}

function ImageViewer({ images }: { images: RecordImage[] }) {
  const [compare, setCompare] = useState(images.length > 1);
  const [left, setLeft] = useState(0);
  const [right, setRight] = useState(1);
  const [expanded, setExpanded] = useState<number | null>(null);
  const expandedImage = expanded === null ? undefined : images[expanded];
  const returnFocusRef = useRef<HTMLButtonElement | null>(null);
  const expand = (index: number, trigger: HTMLButtonElement) => {
    returnFocusRef.current = trigger;
    setExpanded(index);
  };
  return (
    <section aria-label="Record photographs" className="mb-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">
          Photographs{" "}
          <span className="text-muted-foreground">({images.length})</span>
        </h3>
        {images.length > 1 ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-pressed={compare}
            onClick={() => setCompare((value) => !value)}
          >
            Compare images
          </Button>
        ) : null}
      </div>
      <div
        className={cn("grid gap-3", compare ? "sm:grid-cols-2" : "grid-cols-1")}
      >
        <ImagePane
          images={images}
          index={left}
          onChange={setLeft}
          onExpand={(trigger) => expand(left, trigger)}
          label={compare ? "Left image" : "Image"}
        />
        {compare ? (
          <ImagePane
            images={images}
            index={right}
            onChange={setRight}
            onExpand={(trigger) => expand(right, trigger)}
            label="Right image"
          />
        ) : null}
      </div>
      <Dialog
        open={expanded !== null}
        onOpenChange={(open) => {
          if (!open) setExpanded(null);
        }}
      >
        <DialogContent
          finalFocus={returnFocusRef}
          className="max-w-5xl"
          onKeyDown={(event) => {
            // Up/down belong to the enlarged image, never the sheet's record navigation.
            event.stopPropagation();
            if (expanded !== null)
              navigateImage(event, expanded, images.length, setExpanded);
          }}
        >
          {expanded !== null && expandedImage ? (
            <>
              <DialogHeader>
                <DialogTitle>{expandedImage.label}</DialogTitle>
                <DialogDescription>
                  {expandedImage.fileName}
                </DialogDescription>
              </DialogHeader>
              <ZoomImage key={expandedImage.id} image={expandedImage} />
              <ImageNavigation
                label="Enlarged image"
                index={expanded}
                total={images.length}
                onChange={setExpanded}
              />
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function navigateImage(
  event: KeyboardEvent,
  index: number,
  total: number,
  onChange: (index: number) => void,
) {
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
  const next =
    event.key === "ArrowLeft"
      ? index - 1
      : event.key === "ArrowRight"
        ? index + 1
        : null;
  if (next === null) return;
  event.preventDefault();
  event.stopPropagation();
  if (next >= 0 && next < total) onChange(next);
}

function ImagePane({
  images,
  index,
  onChange,
  onExpand,
  label,
}: {
  images: RecordImage[];
  index: number;
  onChange: (index: number) => void;
  onExpand: (trigger: HTMLButtonElement) => void;
  label: string;
}) {
  const image = images[index];
  if (!image) return null;
  return (
    <div
      role="group"
      aria-label={label}
      className="min-w-0 overflow-hidden rounded-lg border border-border"
      onKeyDown={(event) =>
        navigateImage(event, index, images.length, onChange)
      }
    >
      <button
        type="button"
        onClick={(event) => onExpand(event.currentTarget)}
        aria-label={`Enlarge ${image.label}`}
        className="group relative block aspect-[4/3] w-full bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <PreviewImage key={image.id} image={image} />
        <span className="absolute right-2 bottom-2 rounded-md bg-background/90 p-1.5 text-foreground">
          <ExpandIcon className="size-4" />
        </span>
      </button>
      <div className="space-y-1 px-3 py-2">
        <p className="truncate text-sm font-medium" title={image.label}>
          {image.label}
        </p>
        <p
          className="truncate text-xs text-muted-foreground"
          title={image.fileName}
        >
          {image.fileName}
        </p>
        <ImageNavigation
          label={label}
          index={index}
          total={images.length}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

function ImageNavigation({
  label,
  index,
  total,
  onChange,
}: {
  label: string;
  index: number;
  total: number;
  onChange: (index: number) => void;
}) {
  if (total < 2) return null;
  return (
    <div className="flex items-center justify-between gap-2">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Previous ${label.toLowerCase()}`}
        disabled={index === 0}
        onClick={() => onChange(index - 1)}
      >
        <ChevronLeftIcon />
      </Button>
      <span
        aria-live="polite"
        aria-atomic="true"
        className="text-xs tabular-nums text-muted-foreground"
      >
        {index + 1} of {total}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Next ${label.toLowerCase()}`}
        disabled={index === total - 1}
        onClick={() => onChange(index + 1)}
      >
        <ChevronRightIcon />
      </Button>
    </div>
  );
}

function PreviewImage({
  image,
  zoom = false,
}: {
  image: RecordImage;
  zoom?: boolean;
}) {
  const [state, setState] = useState<"loading" | "loaded" | "error">("loading");
  return (
    <>
      {state !== "loaded" ? (
        <span
          role="status"
          className="absolute inset-0 flex items-center justify-center p-4 text-sm text-muted-foreground"
        >
          {state === "error"
            ? "Image unavailable. Access may have changed or the file could not be loaded."
            : "Loading photograph…"}
        </span>
      ) : null}
      {/* Same-origin authenticated bytes; do not send private files to an image optimiser. */}
      <img
        src={`/api/client/files/${encodeURIComponent(image.fileId)}`}
        alt={`${image.label} — ${image.fileName}`}
        decoding="async"
        onLoad={() => setState("loaded")}
        onError={() => setState("error")}
        className={cn(
          "block h-full w-full object-contain",
          state !== "loaded" && "invisible",
          zoom && "max-w-none",
        )}
      />
    </>
  );
}

export function ZoomImage({ image }: { image: RecordImage }) {
  const [scale, setScale] = useState(1);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label="Zoom out"
          disabled={scale === 1}
          onClick={() => setScale((value) => value - 0.5)}
        >
          <MinusIcon />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label="Reset zoom"
          onClick={() => setScale(1)}
        >
          {Math.round(scale * 100)}%
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label="Zoom in"
          disabled={scale === 3}
          onClick={() => setScale((value) => value + 0.5)}
        >
          <PlusIcon />
        </Button>
        <span className="text-xs text-muted-foreground">
          Scroll to inspect when zoomed
        </span>
      </div>
      <div
        tabIndex={0}
        role="region"
        aria-label="Enlarged photograph"
        className="h-[60vh] overflow-auto rounded-md bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div
          className="relative"
          style={{ width: `${scale * 100}%`, height: `${scale * 100}%` }}
        >
          <PreviewImage image={image} zoom />
        </div>
      </div>
    </div>
  );
}
